import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CopyAllRoundedIcon from "@mui/icons-material/CopyAllRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import {
    Alert,
    Box,
    Button,
    ButtonBase,
  IconButton,
    List,
    Link,
    ListItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { alpha, useTheme } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { v7 as uuidv7 } from "uuid";
import {
    deleteTemplateImage,
    fetchTemplate,
    openTemplateImage,
    postTemplate,
    updateTemplate,
    uploadTemplateImage,
} from "../api/createTemplate";
import {
    CancelIconButton,
    DeleteIconButton,
} from "../components/Button";
import FeedbackSnackbar from "../components/FeedbackSnackbar";
import Loading from "../components/Loading";
import RecurrenceFields from "../components/RecurrenceFields";

import { TaskBody, sortTasks } from "../components/TemplateBase";
import { useConfig } from "../contexts/ConfigContext";

/** AppShell の永続ドロワー幅（`AppShell.js` の DRAWER_WIDTH と一致） */
const MAIN_DRAWER_WIDTH_PX = 280;

// ---------------------------------------------------------------------------
// Inline add placeholder (dashed border + "+" button)
// ---------------------------------------------------------------------------

function AddPlaceholder({ label, onClick }) {
  const theme = useTheme();
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
        width: "100%",
        py: 1,
        border: `1.5px dashed ${alpha(theme.palette.primary.main, 0.25)}`,
        borderRadius: 1.5,
        color: "primary.main",
        backgroundColor: alpha(theme.palette.primary.main, 0.03),
        transition: "all 0.15s",
        "&:hover": {
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          borderColor: alpha(theme.palette.primary.main, 0.5),
        },
      }}
    >
      <AddRoundedIcon sx={{ fontSize: 18 }} />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
    </ButtonBase>
  );
}

function normalizeTemplate(template, { includeImages = true } = {}) {
  return {
    ...template,
    template_task_groups: (template.template_task_groups || []).map((group) => ({
      ...group,
      template_tasks: (group.template_tasks || []).map((task) => ({
        ...task,
        recurrence_type: task.recurrence_type || "daily",
        weekly_days: task.weekly_days || [],
        monthly_days: task.monthly_days || [],
        images: includeImages ? task.images || [] : [],
      })),
    })),
  };
}

/** API リクエスト用: ネスト位置で決まる FK は送らない（サーバが付与） */
function templatePayloadForApi(template, { includeImages = true } = {}) {
  const normalized = normalizeTemplate(template, { includeImages });
  return {
    ...normalized,
    name: normalized.name?.trim() || "",
    template_task_groups: (normalized.template_task_groups || []).map((group) => {
      const g = { ...group };
      delete g.template_id;
      g.name = g.name?.trim() || "";
      g.template_tasks = (group.template_tasks || []).map((task) => {
        const t = { ...task };
        delete t.template_task_group_id;
        t.name = t.name?.trim() || "";
        t.detail = typeof t.detail === "string" ? t.detail.trim() || null : t.detail ?? null;
        t.images = (t.images || []).map((image, index) => ({
          ...image,
          order: index,
        }));
        return t;
      });
      return g;
    }),
  };
}

function createTaskDraft() {
  return {
    name: "",
    detail: null,
    recurrence_type: "daily",
    weekly_days: [],
    monthly_days: [],
    images: [],
  };
}

function createTaskSnapshot(task) {
  return {
    name: task.name || "",
    detail: task.detail || null,
    recurrence_type: task.recurrence_type || "daily",
    weekly_days: task.weekly_days || [],
    monthly_days: task.monthly_days || [],
    images: (task.images || []).map((img, i) => ({
      ...img,
      id: img.id || `${task.id}-img-${i}`,
      order: img.order ?? i,
    })),
  };
}

function validateTask(task) {
  if (!task.name?.trim()) {
    return "作業項目名を入力してください。";
  }
  if (task.recurrence_type === "weekly" && (task.weekly_days || []).length === 0) {
    return "毎週を選ぶ場合は曜日を1つ以上選択してください。";
  }
  if (task.recurrence_type === "monthly" && (task.monthly_days || []).length === 0) {
    return "毎月を選ぶ場合は日付を1つ以上選択してください。";
  }
  return "";
}

function validateTemplate(template) {
  if (!template.name?.trim()) {
    return "テンプレート名を入力してください。";
  }

  for (const group of template.template_task_groups || []) {
    if (!group.name?.trim()) {
      return "中分類名を入力してください。";
    }

    for (const task of group.template_tasks || []) {
      const error = validateTask(task);
      if (error) {
        return error;
      }
    }
  }

  return "";
}

function createEmptyTemplate() {
  return {
    id: uuidv7(),
    name: "",
    template_task_groups: [],
  };
}

function cloneTemplateForCreate(template) {
  const nextTemplateId = uuidv7();

  return {
    ...template,
    id: nextTemplateId,
    public_id: undefined,
    template_task_groups: (template.template_task_groups || []).map((group, groupIndex) => {
      const nextGroupId = uuidv7();

      return {
        ...group,
        id: nextGroupId,
        order: group.order ?? groupIndex,
        template_tasks: (group.template_tasks || []).map((task, taskIndex) => ({
          ...task,
          id: uuidv7(),
          order: task.order ?? taskIndex,
          images: (task.images || []).map((image, imageIndex) => ({
            ...image,
            id: uuidv7(),
            public_id: undefined,
            order: image.order ?? imageIndex,
          })),
        })),
      };
    }),
  };
}

// ---------------------------------------------------------------------------
// Task component (single task item, draggable)
// ---------------------------------------------------------------------------

function Task({
  task,
  groupId,
  index,
  removeTask,
  duplicateTask,
  updateTask,
  shouldAutoEdit,
  isDraftTask,
  onAutoEditHandled,
  onTaskEditCompleted,
}) {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editPanel, setEditPanel] = useState("text");
  const [taskError, setTaskError] = useState("");
  const [editSnapshot, setEditSnapshot] = useState(null);
  const [isUploadingTaskImage, setIsUploadingTaskImage] = useState(false);

  const config = useConfig();
  const uploadImageEnabled = Boolean(config?.features?.enableUploadImage);

  const startEdit = () => {
    setEditSnapshot(createTaskSnapshot(task));
    setEditPanel("text");
    setTaskError("");
    setIsEditing(true);
  };

  const cancelEdit = () => {
    if (isDraftTask) {
      removeTask(task.id);
      onTaskEditCompleted?.(task.id);
      return;
    }
    if (editSnapshot) {
      updateTask(groupId, task.id, editSnapshot);
    }
    setIsEditing(false);
    setEditPanel("text");
    setTaskError("");
    onTaskEditCompleted?.(task.id);
  };

  const completeEdit = () => {
    const error = validateTask(task);
    if (error) {
      setTaskError(error);
      return;
    }
    setTaskError("");
    setIsEditing(false);
    onTaskEditCompleted?.(task.id);
  };

  useEffect(() => {
    if (!shouldAutoEdit || isEditing) return;
    setEditSnapshot(createTaskSnapshot(task));
    setEditPanel("text");
    setTaskError("");
    setIsEditing(true);
    onAutoEditHandled?.(task.id);
  }, [shouldAutoEdit, isEditing, task, task.id, onAutoEditHandled]);

  const handleTaskImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setIsUploadingTaskImage(true);
    try {
      const response = await uploadTemplateImage(file);
      updateTask(groupId, task.id, {
        images: [
          ...(task.images || []),
          {
            id: uuidv7(),
            display_name: response.display_name,
            path: response.path,
            order: (task.images || []).length,
          },
        ],
      });
      event.target.value = "";
    } catch (error) {
      console.error("画像アップロードエラー:", error);
      setTaskError(
        error.message || "画像のアップロードに失敗しました。"
      );
      event.target.value = "";
    } finally {
      setIsUploadingTaskImage(false);
    }
  };

  const handleTaskRemoveImage = async (image) => {
    if (image.public_id) {
      try {
        await deleteTemplateImage(image.public_id);
      } catch (error) {
        console.error("画像削除エラー:", error);
      }
    }
    updateTask(groupId, task.id, {
      images: (task.images || []).filter((img) => img.id !== image.id),
    });
  };

  const handleTaskImageDisplayNameChange = (imageId, newName) => {
    updateTask(groupId, task.id, {
      images: (task.images || []).map((img) =>
        img.id === imageId ? { ...img, display_name: newName } : img
      ),
    });
  };

  const handleTaskImageOpen = async (imagePath) => {
    try {
      setTaskError("");
      await openTemplateImage(imagePath);
    } catch (error) {
      console.error("画像表示エラー:", error);
      setTaskError(error.message || "画像の表示に失敗しました。");
    }
  };

  const handleDuplicateClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    duplicateTask(groupId, task.id);
  };

  return (
    <Draggable draggableId={task.id} index={index}>
      {(provided, snapshot) => (
        <ListItem
          ref={provided.innerRef}
          {...provided.draggableProps}
          dense
          disablePadding
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            py: 0.75,
            px: 1,
            mb: 0.5,
            borderRadius: 1.5,
            border: `1px solid ${alpha(theme.palette.primary.dark, 0.1)}`,
            backgroundColor: snapshot.isDragging
              ? alpha(theme.palette.primary.main, 0.06)
              : "#fff",
            boxShadow: snapshot.isDragging
              ? `0 6px 16px ${alpha(theme.palette.primary.dark, 0.1)}`
              : "none",
            transition: "box-shadow 0.15s, background-color 0.15s",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.25,
              flexShrink: 0,
            }}
          >
            <Box
              {...provided.dragHandleProps}
              sx={{
                display: "flex",
                alignItems: "center",
                color: "text.secondary",
                cursor: "grab",
              }}
            >
              <DragIndicatorRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            {!isEditing && (
              <IconButton
                aria-label="copy task"
                size="small"
                onClick={handleDuplicateClick}
                sx={{ color: "text.secondary" }}
              >
                <CopyAllRoundedIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {isEditing ? (
              <Stack spacing={1}>
                {taskError && <Alert severity="error">{taskError}</Alert>}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    variant={editPanel === "text" ? "contained" : "outlined"}
                    onClick={() => setEditPanel("text")}
                  >
                    テキスト
                  </Button>
                  {uploadImageEnabled && (
                    <Button
                      size="small"
                      variant={editPanel === "image" ? "contained" : "outlined"}
                      onClick={() => setEditPanel("image")}
                    >
                      画像
                    </Button>
                  )}
                </Box>

                {editPanel === "text" || !uploadImageEnabled ? (
                  <>
                    <TextField
                      size="small"
                      label="作業項目名"
                      value={task.name || ""}
                      onChange={(e) => {
                        setTaskError("");
                        updateTask(groupId, task.id, { name: e.target.value });
                      }}
                      fullWidth
                    />
                    <TextField
                      size="small"
                      label="説明"
                      value={task.detail || ""}
                      onChange={(e) =>
                        updateTask(groupId, task.id, { detail: e.target.value })
                      }
                      fullWidth
                      multiline
                      minRows={2}
                    />
                    <RecurrenceFields
                      idPrefix={`task-${task.id}`}
                      recurrenceType={task.recurrence_type || "daily"}
                      weeklyDays={task.weekly_days || []}
                      monthlyDays={task.monthly_days || []}
                      onRecurrenceTypeChange={(value) => {
                        setTaskError("");
                        updateTask(groupId, task.id, {
                          recurrence_type: value,
                          weekly_days: value === "weekly" ? task.weekly_days || [] : [],
                          monthly_days: value === "monthly" ? task.monthly_days || [] : [],
                        });
                      }}
                      onWeeklyDaysChange={(value) => {
                        setTaskError("");
                        updateTask(groupId, task.id, { weekly_days: value });
                      }}
                      onMonthlyDaysChange={(value) => {
                        setTaskError("");
                        updateTask(groupId, task.id, { monthly_days: value });
                      }}
                    />
                  </>
                ) : (
                  <Box>
                    <Box
                      sx={{
                        border: `2px dashed ${alpha(theme.palette.primary.main, 0.25)}`,
                        p: 1.5,
                        textAlign: "center",
                        borderRadius: 1.5,
                        backgroundColor: alpha(theme.palette.primary.main, 0.03),
                        mb: 1,
                      }}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleTaskImageUpload}
                        style={{ display: "none" }}
                        id={`task-image-upload-${task.id}`}
                        disabled={isUploadingTaskImage}
                      />
                      <label htmlFor={`task-image-upload-${task.id}`}>
                        <Button
                          component="span"
                          variant="contained"
                          size="small"
                          disabled={isUploadingTaskImage}
                        >
                          {isUploadingTaskImage ? "アップロード中..." : "画像を追加"}
                        </Button>
                      </label>
                    </Box>

                    {(task.images || []).length > 0 ? (
                      <Paper variant="outlined" sx={{ p: 1 }}>
                        <Typography variant="caption">追加された画像:</Typography>
                        <List dense disablePadding>
                          {(task.images || []).map((img) => (
                            <ListItem
                              key={img.id}
                              secondaryAction={
                                <DeleteIconButton
                                  edge="end"
                                  onClick={() => handleTaskRemoveImage(img)}
                                />
                              }
                              sx={{ pl: 0 }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                                <TextField
                                  size="small"
                                  variant="standard"
                                  value={img.display_name || ""}
                                  onChange={(e) =>
                                    handleTaskImageDisplayNameChange(img.id, e.target.value)
                                  }
                                  sx={{ flexGrow: 1, mr: 1 }}
                                  InputProps={{ disableUnderline: true }}
                                />
                                <Link
                                  component="button"
                                  type="button"
                                  onClick={() => handleTaskImageOpen(img.path)}
                                  variant="caption"
                                  sx={{
                                    color: "primary.main",
                                    textDecoration: "underline",
                                    ml: 1,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  (表示)
                                </Link>
                              </Box>
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        画像がありません。
                      </Typography>
                    )}
                  </Box>
                )}
              </Stack>
            ) : (
              <Box
                onClick={startEdit}
                sx={{
                  cursor: "pointer",
                  borderRadius: 1,
                  px: 0.5,
                  mx: -0.5,
                  "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.06) },
                }}
              >
                <TaskBody
                  name={task.name}
                  detail={task.detail}
                  images={uploadImageEnabled ? task.images : []}
                  recurrenceType={task.recurrence_type}
                  weeklyDays={task.weekly_days}
                  monthlyDays={task.monthly_days}
                  compact
                />
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}>
            {isEditing ? (
              <>
                <IconButton
                  aria-label="complete task editing"
                  color="primary"
                  size="small"
                  onClick={completeEdit}
                >
                  <CheckRoundedIcon fontSize="small" />
                </IconButton>
                <CancelIconButton size="small" onClick={cancelEdit} />
              </>
            ) : (
              <DeleteIconButton size="small" onClick={() => removeTask(task.id)} />
            )}
          </Box>
        </ListItem>
      )}
    </Draggable>
  );
}

// ---------------------------------------------------------------------------
// TaskList (droppable list of tasks within a group)
// ---------------------------------------------------------------------------

function TaskList({ template, groupId, removeTask, duplicateTask, updateTask, addTask }) {
  const [autoEditingTaskId, setAutoEditingTaskId] = useState(null);
  const [draftTaskId, setDraftTaskId] = useState(null);
  const group = template.template_task_groups.find((g) => g.id === groupId);

  const handleAdd = () => {
    const newTaskId = addTask(groupId, {
      ...createTaskDraft(),
    });
    setDraftTaskId(newTaskId);
    setAutoEditingTaskId(newTaskId);
  };

  const handleAutoEditHandled = (taskId) => {
    setAutoEditingTaskId((prev) => (prev === taskId ? null : prev));
  };

  const handleTaskEditCompleted = (taskId) => {
    setDraftTaskId((prev) => (prev === taskId ? null : prev));
  };

  return (
    <Box sx={{ pl: { xs: 0.5, sm: 2 }, pt: 0.75 }}>
      <Droppable droppableId={groupId} type="TASK">
        {(provided) => (
          <List
            ref={provided.innerRef}
            {...provided.droppableProps}
            disablePadding
            sx={{ minHeight: 2 }}
          >
            {group.template_tasks.map((task, index) => (
              <Task
                key={task.id}
                task={task}
                groupId={groupId}
                index={index}
                removeTask={removeTask}
                duplicateTask={duplicateTask}
                updateTask={updateTask}
                shouldAutoEdit={autoEditingTaskId === task.id}
                isDraftTask={draftTaskId === task.id}
                onAutoEditHandled={handleAutoEditHandled}
                onTaskEditCompleted={handleTaskEditCompleted}
              />
            ))}
            {provided.placeholder}
          </List>
        )}
      </Droppable>

      <Box sx={{ mt: 0.25 }}>
        <AddPlaceholder
          label="作業項目を追加"
          onClick={handleAdd}
        />
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// GroupList (single group header + tasks, draggable)
// ---------------------------------------------------------------------------

function GroupList({
  groupId,
  groupName,
  index,
  taskList,
  removeGroup,
  updateGroup,
  shouldAutoEdit,
  isDraftGroup,
  onAutoEditHandled,
  onGroupEditCompleted,
}) {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [groupError, setGroupError] = useState("");
  const [editSnapshotName, setEditSnapshotName] = useState(groupName || "");

  const startEdit = () => {
    setEditSnapshotName(groupName || "");
    setGroupError("");
    setIsEditing(true);
  };

  const completeEdit = () => {
    if (!groupName.trim()) {
      setGroupError("中分類名を入力してください。");
      return;
    }
    setGroupError("");
    setIsEditing(false);
    onGroupEditCompleted?.(groupId);
  };

  const cancelEdit = () => {
    if (isDraftGroup) {
      removeGroup(groupId);
      onGroupEditCompleted?.(groupId);
      return;
    }
    updateGroup(groupId, { name: editSnapshotName });
    setIsEditing(false);
    setGroupError("");
    onGroupEditCompleted?.(groupId);
  };

  useEffect(() => {
    if (!shouldAutoEdit || isEditing) return;
    setEditSnapshotName(groupName || "");
    setGroupError("");
    setIsEditing(true);
    onAutoEditHandled?.(groupId);
  }, [shouldAutoEdit, isEditing, groupId, groupName, onAutoEditHandled]);

  return (
    <Draggable draggableId={groupId} index={index}>
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          sx={{
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.dark, 0.1)}`,
            backgroundColor: snapshot.isDragging
              ? alpha(theme.palette.primary.main, 0.04)
              : alpha(theme.palette.primary.main, 0.02),
            py: 0.75,
            px: 1.25,
            transition: "box-shadow 0.15s, background-color 0.15s",
            boxShadow: snapshot.isDragging
              ? `0 8px 24px ${alpha(theme.palette.primary.dark, 0.1)}`
              : "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1, minWidth: 0 }}>
              <Box
                {...provided.dragHandleProps}
                sx={{ display: "flex", color: "text.secondary", cursor: "grab", flexShrink: 0 }}
              >
                <DragIndicatorRoundedIcon />
              </Box>
              {isEditing ? (
                <TextField
                  size="small"
                  label="中分類名"
                  value={groupName || ""}
                  onChange={(e) => {
                    setGroupError("");
                    updateGroup(groupId, { name: e.target.value });
                  }}
                  sx={{ minWidth: 200 }}
                  error={Boolean(groupError)}
                  helperText={groupError}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      completeEdit();
                    }
                    if (e.key === "Escape") {
                      cancelEdit();
                    }
                  }}
                />
              ) : (
                <Box
                  sx={{
                    flexGrow: 1,
                    cursor: "pointer",
                    borderRadius: 1,
                    px: 0.5,
                    mx: -0.5,
                    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.06) },
                  }}
                  onClick={startEdit}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {groupName}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 0.25, alignItems: "center", flexShrink: 0 }}>
              {isEditing ? (
                <>
                  <IconButton
                    aria-label="complete group editing"
                    color="primary"
                    size="small"
                    onClick={completeEdit}
                  >
                    <CheckRoundedIcon fontSize="small" />
                  </IconButton>
                  <CancelIconButton size="small" onClick={cancelEdit} />
                </>
              ) : (
                <DeleteIconButton size="small" onClick={() => removeGroup(groupId)} />
              )}
            </Box>
          </Box>
          {taskList}
        </Box>
      )}
    </Draggable>
  );
}

// ---------------------------------------------------------------------------
// Main CreateTemplate page
// ---------------------------------------------------------------------------

function CreateTemplate() {
  const [template, setTemplate] = useState(() => createEmptyTemplate());

  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [draftGroupId, setDraftGroupId] = useState(null);
  const [autoEditingGroupId, setAutoEditingGroupId] = useState(null);
  const { templatePublicId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const config = useConfig();
  const uploadImageEnabled = Boolean(config?.features?.enableUploadImage);
  const baseTemplatePublicId = searchParams.get("baseTemplate");
  const isEditing = Boolean(templatePublicId);
  const sourceTemplatePublicId = templatePublicId || baseTemplatePublicId;

  const showFeedback = (message, severity = "info") => {
    setFeedback({ open: true, message, severity });
  };

  useEffect(() => {
    const init = async () => {
      if (!sourceTemplatePublicId) {
        setTemplate(createEmptyTemplate());
        return;
      }

      try {
        setIsLoading(true);
        const data = await fetchTemplate(sourceTemplatePublicId);
        const normalized = normalizeTemplate(data, {
          includeImages: uploadImageEnabled,
        });
        setTemplate(isEditing ? normalized : cloneTemplateForCreate(normalized));
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [sourceTemplatePublicId, isEditing, uploadImageEnabled]);

  if (isLoading) {
    return <Loading />;
  }

  // --- State mutations ---

  const handleTemplateNameChange = (e) => {
    setTemplate((prev) => ({ ...prev, name: e.target.value }));
  };

  const addGroup = (name = "") => {
    const newGroupId = uuidv7();
    setTemplate((prev) =>
      sortTasks({
        ...prev,
        template_task_groups: [
          ...prev.template_task_groups,
          {
            id: newGroupId,
            name,
            template_tasks: [],
            order: prev.template_task_groups.length,
          },
        ],
      })
    );
    return newGroupId;
  };

  const removeGroup = (groupId) => {
    setDraftGroupId((prev) => (prev === groupId ? null : prev));
    setAutoEditingGroupId((prev) => (prev === groupId ? null : prev));
    setTemplate((prev) => {
      const updated = prev.template_task_groups
        .filter((g) => g.id !== groupId)
        .map((g, i) => ({ ...g, order: i }));
      return sortTasks({ ...prev, template_task_groups: updated });
    });
  };

  const updateGroup = (groupId, updates) => {
    setTemplate((prev) => {
      const newGroups = prev.template_task_groups.map((g) =>
        g.id === groupId ? { ...g, ...updates } : g
      );
      return sortTasks({ ...prev, template_task_groups: newGroups });
    });
  };

  const addTask = (groupId, taskData) => {
    const newTaskId = uuidv7();
    setTemplate((prev) => {
      const newGroups = prev.template_task_groups.map((g) => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          template_tasks: [
            ...g.template_tasks,
            { ...taskData, id: newTaskId, order: g.template_tasks.length },
          ],
        };
      });
      return sortTasks({ ...prev, template_task_groups: newGroups });
    });
    return newTaskId;
  };

  const duplicateTask = (groupId, taskId) => {
    const newTaskId = uuidv7();

    setTemplate((prev) => {
      const newGroups = prev.template_task_groups.map((group) => {
        if (group.id !== groupId) return group;

        const sourceIndex = group.template_tasks.findIndex((task) => task.id === taskId);
        if (sourceIndex === -1) return group;

        const sourceTask = group.template_tasks[sourceIndex];
        const duplicatedTask = {
          ...sourceTask,
          id: newTaskId,
          images: (sourceTask.images || []).map((img, index) => ({
            ...img,
            id: uuidv7(),
            order: index,
          })),
        };

        const tasks = [...group.template_tasks];
        tasks.splice(sourceIndex + 1, 0, duplicatedTask);

        return {
          ...group,
          template_tasks: tasks.map((task, index) => ({
            ...task,
            order: index,
          })),
        };
      });

      return sortTasks({ ...prev, template_task_groups: newGroups });
    });

    return newTaskId;
  };

  const removeTask = (taskId) => {
    setTemplate((prev) => {
      const newGroups = prev.template_task_groups.map((g) => {
        const updated = g.template_tasks
          .filter((t) => t.id !== taskId)
          .map((t, i) => ({ ...t, order: i }));
        return { ...g, template_tasks: updated };
      });
      return sortTasks({ ...prev, template_task_groups: newGroups });
    });
  };

  const updateTask = (groupId, taskId, updates) => {
    setTemplate((prev) => {
      const newGroups = prev.template_task_groups.map((g) => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          template_tasks: g.template_tasks.map((t) =>
            t.id === taskId ? { ...t, ...updates } : t
          ),
        };
      });
      return sortTasks({ ...prev, template_task_groups: newGroups });
    });
  };

  // --- Drag & Drop ---

  const handleDragEnd = (result) => {
    const { source, destination, type } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    if (type === "GROUP") {
      setTemplate((prev) => {
        const groups = [...prev.template_task_groups];
        const [moved] = groups.splice(source.index, 1);
        groups.splice(destination.index, 0, moved);
        groups.forEach((g, i) => { g.order = i; });
        return { ...prev, template_task_groups: groups };
      });
      return;
    }

    if (type === "TASK") {
      setTemplate((prev) => {
        const srcGroupId = source.droppableId;
        const dstGroupId = destination.droppableId;

        if (srcGroupId === dstGroupId) {
          const newGroups = prev.template_task_groups.map((g) => {
            if (g.id !== srcGroupId) return g;
            const tasks = [...g.template_tasks];
            const [moved] = tasks.splice(source.index, 1);
            tasks.splice(destination.index, 0, moved);
            tasks.forEach((t, i) => { t.order = i; });
            return { ...g, template_tasks: tasks };
          });
          return { ...prev, template_task_groups: newGroups };
        }

        const srcGroup = prev.template_task_groups.find((g) => g.id === srcGroupId);
        const movedTask = srcGroup.template_tasks[source.index];

        const newGroups = prev.template_task_groups.map((g) => {
          if (g.id === srcGroupId) {
            const tasks = g.template_tasks.filter((_, i) => i !== source.index);
            tasks.forEach((t, i) => { t.order = i; });
            return { ...g, template_tasks: tasks };
          }
          if (g.id === dstGroupId) {
            const tasks = [...g.template_tasks];
            tasks.splice(destination.index, 0, movedTask);
            tasks.forEach((t, i) => { t.order = i; });
            return { ...g, template_tasks: tasks };
          }
          return g;
        });
        return { ...prev, template_task_groups: newGroups };
      });
    }
  };

  // --- Save ---

  const handleSave = async () => {
    const validationError = validateTemplate(template);
    if (validationError) {
      showFeedback(validationError, "warning");
      return;
    }
    setIsLoading(true);
    try {
      const payload = templatePayloadForApi(template, {
        includeImages: uploadImageEnabled,
      });
      if (isEditing) {
        await updateTemplate(payload);
      } else {
        await postTemplate(payload);
      }
      setIsLoading(false);
      setFeedback({
        open: true,
        message: "テンプレートを保存しました。",
        severity: "success",
        action: (
          <Button color="inherit" size="small" onClick={() => navigate("/")}>
            ホームへ
          </Button>
        ),
      });
    } catch (error) {
      console.error("テンプレート保存中にエラーが発生しました:", error);
      setIsLoading(false);
      showFeedback("テンプレートの保存に失敗しました。", "error");
    }
  };

  const handleAddGroup = () => {
    const newGroupId = addGroup("");
    setDraftGroupId(newGroupId);
    setAutoEditingGroupId(newGroupId);
  };

  const handleGroupAutoEditHandled = (groupId) => {
    setAutoEditingGroupId((prev) => (prev === groupId ? null : prev));
  };

  const handleGroupEditCompleted = (groupId) => {
    setDraftGroupId((prev) => (prev === groupId ? null : prev));
  };

  // --- Render ---

  return (
    <>
      <Stack spacing={2} sx={{ pb: 12 }}>
        <TextField
          variant="outlined"
          value={template.name}
          onChange={handleTemplateNameChange}
          placeholder="テンプレート名を入力"
          label="テンプレート名"
        />

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="groups" type="GROUP">
            {(provided) => (
              <Stack
                ref={provided.innerRef}
                {...provided.droppableProps}
                spacing={1}
              >
                {template.template_task_groups.map((group, index) => (
                  <GroupList
                    key={group.id}
                    groupId={group.id}
                    groupName={group.name}
                    index={index}
                    taskList={
                      <TaskList
                        template={template}
                        groupId={group.id}
                        removeTask={removeTask}
                        duplicateTask={duplicateTask}
                        updateTask={updateTask}
                        addTask={addTask}
                      />
                    }
                    removeGroup={removeGroup}
                    updateGroup={updateGroup}
                    shouldAutoEdit={autoEditingGroupId === group.id}
                    isDraftGroup={draftGroupId === group.id}
                    onAutoEditHandled={handleGroupAutoEditHandled}
                    onGroupEditCompleted={handleGroupEditCompleted}
                  />
                ))}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>

        <AddPlaceholder
          label="中分類を追加"
          onClick={handleAddGroup}
        />
      </Stack>

      <Paper
        component="footer"
        elevation={0}
        square
        sx={{
          position: "fixed",
          bottom: 0,
          right: 0,
          left: { xs: 0, lg: `${MAIN_DRAWER_WIDTH_PX}px` },
          zIndex: (theme) => theme.zIndex.appBar - 1,
          px: { xs: 2, sm: 3, lg: 4 },
          py: 1.5,
          pb: {
            xs: "calc(12px + env(safe-area-inset-bottom, 0px))",
            sm: "calc(12px + env(safe-area-inset-bottom, 0px))",
          },
          borderTop: 1,
          borderColor: "divider",
          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.92),
          backdropFilter: "blur(14px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            maxWidth: { lg: 1200 },
            mx: { lg: "auto" },
          }}
        >
          <Button
            variant="contained"
            onClick={handleSave}
            size="large"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ width: { xs: "100%", sm: "auto" }, minWidth: { sm: 180 } }}
          >
            {isLoading ? "保存中..." : "保存する"}
          </Button>
        </Box>
      </Paper>

      <FeedbackSnackbar
        open={feedback.open}
        message={feedback.message}
        severity={feedback.severity}
        action={feedback.action}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
}

export default CreateTemplate;
